import { ft } from '../core/units.js';
import { DELIVERIES } from '../data/deliveries.js';

/**
 * A load against a clock, and the cart it is on.
 *
 * Posting one sends the row's cart home and loads it, and starts the clock.
 * Nothing about moving it is here: it is a cart like any cart, pushed, pulled
 * down the stairs, towed, cornered and bumped by `wheeled.js`, and a load that
 * goes over is on the ground like any load. This file only notices when the
 * cart is stood where the load was wanted, counts what is on it, and asks the
 * clock how much of that is still any good.
 *
 *   delivered   the cart stood where the row says, with some of the load on it
 *   melted      the clock ran out first
 */

const clockOf = (clock) => clock.day * 1440 + clock.minutes;

/** Metres per second. A cart rolling faster than this is going past, not arriving. */
const STOOD = 0.6;

export function createDeliveries({ wheeled, level, clock, standing, hud, rows = DELIVERIES }) {
  let active = null;
  let records = [];

  const rowOf = (id) => rows.find((row) => row.id === id) ?? null;

  /**
   * How much of the load is still good, from nothing to one.
   *
   * Slow at first and faster later, the way bagged ice in a bin goes: the
   * first hour takes the edges off it and the last hour is water. Nothing is
   * left at the row's `melts`.
   */
  function goodness(row) {
    if (!active) return 0;
    const gone = (clockOf(clock) - active.from) / row.melts;
    return Math.max(0, 1 - gone * gone);
  }

  function close(outcome, delivered = 0) {
    const row = rowOf(active.id);
    const good = goodness(row);
    const share = outcome === 'delivered' ? (delivered / row.load.count) * good : 0;
    records.push({ id: active.id, day: active.day, outcome, at: clock.minutes, delivered, good: Math.round(good * 100) / 100, share: Math.round(share * 100) / 100 });
    if (outcome === 'delivered') {
      const scaled = Object.fromEntries(Object.entries(row.award).map(([department, amount]) => [department, amount * share]));
      standing?.applyAll(scaled, { reason: `${delivered} of ${row.load.count} delivered to the Caytons` });
      hud?.say(`Delivered: ${delivered} of ${row.load.count}, ${Math.round(good * 100)}% of it still ice.`, { tone: share > 0.5 ? 'good' : 'warn', seconds: 5 });
    } else {
      standing?.applyAll(row.melted ?? {}, { reason: 'The ice never got there' });
      hud?.say('The ice is water. Whatever got there is no good to anybody.', { tone: 'bad', seconds: 5 });
    }
    active = null;
  }

  return {
    rows,

    /** A load wanted somewhere. From a beat, when it is played. */
    post(id) {
      const row = rowOf(id);
      if (!row || active) return false;
      const cart = wheeled.byId(row.cart);
      if (!cart) return false;
      wheeled.home(cart);
      cart.hold.clear();
      cart.hold.add(row.load.kind, null, row.load.count);
      active = { id, day: clock.day, from: clockOf(clock) };
      return true;
    },

    get active() {
      return active ? { ...active } : null;
    },

    get records() {
      return records.map((record) => ({ ...record }));
    },

    /** Zones the job you are on puts you in. */
    works(zone) {
      return Boolean(active && rowOf(active.id)?.works.includes(zone));
    },

    /** The load as numbers, for the objective and a check. */
    get reading() {
      if (!active) return null;
      const row = rowOf(active.id);
      const cart = wheeled.byId(row.cart);
      return {
        id: row.id,
        title: row.title,
        on: cart?.hold.kind === row.load.kind ? cart.hold.count : 0,
        of: row.load.count,
        good: Math.round(goodness(row) * 100) / 100,
        meltsAt: (active.from + row.melts) % 1440,
        cart: cart ? { x: cart.position.x, z: cart.position.z, room: level.roomAt(cart.position.x, cart.position.z)?.id ?? null } : null,
        to: { x: ft(row.to.at[0]), z: ft(row.to.at[1]), room: row.to.room },
      };
    },

    update() {
      if (!active) return;
      const row = rowOf(active.id);
      if (goodness(row) <= 0) return close('melted');
      const cart = wheeled.byId(row.cart);
      if (!cart || cart.tipped) return;
      const room = level.roomAt(cart.position.x, cart.position.z)?.id ?? null;
      if (room !== row.to.room) return;
      const gap = Math.hypot(cart.position.x - ft(row.to.at[0]), cart.position.z - ft(row.to.at[1]));
      const speed = Math.hypot(cart.velocity.x, cart.velocity.z);
      if (gap > ft(row.to.within) || speed > STOOD) return;
      const on = cart.hold.kind === row.load.kind ? cart.hold.count : 0;
      if (!on) return;
      // Taken off the cart where it was wanted.
      cart.hold.clear();
      close('delivered', on);
    },

    toJSON: () => ({ active, records }),
    load(state) {
      active = state?.active ? { ...state.active } : null;
      records = (state?.records ?? []).map((record) => ({ ...record }));
    },
    clear() {
      active = null;
      records = [];
    },
  };
}
